import { useEffect, useRef, useState } from "react";
import { EMAIL_FRAME_SANDBOX, emailFrameDocument } from "../../src/email-frame.ts";

/**
 * The forwarded email under the rep's words, editable as any client lets it be: parent-owned editing
 * in the existing scriptless mail sandbox, so the frame cannot fetch or execute. The frame is as tall
 * as the email, so it scrolls with the words above it instead of inside a box of its own.
 */
export function ForwardEditor(props: {
  html: string;
  disabled: boolean;
  onChange: (html: string, text: string, unsupportedImages: boolean) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [initial] = useState(() => emailFrameDocument(props.html, { images: false, quoted: true }));
  const change = useRef(props.onChange);
  change.current = props.onChange;
  const [loaded, setLoaded] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const body = frame.current?.contentDocument?.body;
    if (!loaded || !body) return;
    const fit = () =>
      setHeight(Math.ceil(frame.current?.contentDocument?.documentElement.scrollHeight ?? 0));
    fit();
    body.contentEditable = String(!props.disabled);
    body.setAttribute("role", "textbox");
    body.setAttribute("aria-label", "Forwarded email");
    body.setAttribute("aria-multiline", "true");
    const input = () => {
      fit();
      change.current(
        body.innerHTML,
        body.innerText,
        [...body.querySelectorAll("img")].some(
          (image) =>
            !/^data:image\/(?:png|jpeg|gif|webp);base64,[a-zA-Z0-9+/=]+$/u.test(
              image.getAttribute("src") ?? "",
            ),
        ),
      );
    };
    body.addEventListener("input", input);
    return () => body.removeEventListener("input", input);
  }, [loaded, props.disabled]);
  return (
    <iframe
      ref={frame}
      title="Forwarded email"
      className="lcr-i2-forward-editor"
      sandbox={EMAIL_FRAME_SANDBOX}
      srcDoc={initial}
      style={height ? { height } : undefined}
      onLoad={() => setLoaded(true)}
    />
  );
}
